/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { CSSProperties, ReactElement } from 'react';
import { cn } from '@/lib/utils';

const PINTEREST_PATH =
    'M11.98 23.5869C5.36253 23.5869 0 18.3057 0 11.7928C0 5.28 5.36253 0 11.98 0C18.5963 0 23.9601 5.28 23.9601 11.7928C23.9601 18.3057 18.5963 23.5869 11.98 23.5869ZM1.10844 11.793C1.10844 16.3201 3.96999 20.1879 8.00824 21.7454C7.91329 20.9003 7.8286 19.6031 8.04674 18.6784C8.24307 17.8448 9.31968 13.3669 9.31968 13.3669C9.31968 13.3669 8.99503 12.7264 8.99503 11.7803C8.99503 10.2936 9.8689 9.18455 10.9596 9.18455C11.8848 9.18455 12.3327 9.86918 12.3327 10.6902C12.3327 11.6073 11.7398 12.9778 11.4331 14.2485C11.1778 15.3134 11.9759 16.1799 13.041 16.1799C14.9709 16.1799 16.4543 14.1765 16.4543 11.2852C16.4543 8.72602 14.5873 6.93739 11.9195 6.93739C8.83078 6.93739 7.01761 9.21865 7.01761 11.5757C7.01761 12.494 7.37562 13.4793 7.82475 14.0149C7.91329 14.121 7.92612 14.2132 7.90045 14.3218C7.81833 14.6591 7.63483 15.3854 7.5989 15.5332C7.55142 15.729 7.44107 15.7707 7.23447 15.6772C5.8794 15.0557 5.03249 13.1029 5.03249 11.5365C5.03249 8.16518 7.52063 5.06792 12.2056 5.06792C15.9718 5.06792 18.8988 7.71044 18.8988 11.241C18.8988 14.9243 16.539 17.889 13.2655 17.889C12.1646 17.889 11.1303 17.3256 10.7761 16.6612C10.7761 16.6612 10.232 18.7012 10.0999 19.2014C9.85478 20.1298 9.19265 21.2932 8.75122 22.0031C9.76624 22.3138 10.8454 22.4805 11.9657 22.4805C17.9621 22.4805 22.8229 17.6957 22.8229 11.793C22.8229 5.88897 17.9621 1.10539 11.9657 1.10539C5.96923 1.10539 1.10844 5.88897 1.10844 11.793Z';

interface PinterestIconProps {
    className?: string;
    style?: CSSProperties;
    'aria-hidden'?: boolean;
}

export function PinterestIcon({
    className,
    style,
    'aria-hidden': ariaHidden = true,
}: PinterestIconProps): ReactElement {
    return (
        <svg
            viewBox="0 0 23.9601 23.5869"
            fill="currentColor"
            className={cn('size-6', className)}
            style={style}
            aria-hidden={ariaHidden}>
            <path fillRule="evenodd" clipRule="evenodd" d={PINTEREST_PATH} />
        </svg>
    );
}
